import axios from 'axios';
import * as constants from '../../constants/CONSTANT';

/** Record-view / role assignment uses detail layouts only (not edit layouts). */
export function filterLayoutsDetailOnly(layouts) {
  return (layouts || []).filter(
    (lo) => String(lo.layout_type || '').trim().toUpperCase() === 'DETAIL'
  );
}

export function getCrmAuthHeaders() {
  const raw = localStorage.getItem('token');
  if (!raw || raw === 'null' || raw === 'undefined') return {};
  return { Authorization: raw };
}

export async function fetchRecordViewConfigRows(roleId) {
  const res = await axios.get(
    `${constants.API_BASE_URL}/api/permissions/role/${roleId}/recordview-config`,
    { headers: getCrmAuthHeaders() }
  );
  return res.data?.success ? res.data.data || [] : [];
}

/** PUT role recordview-config; returns axios response body `{ success, data?, message? }`. */
export async function saveRecordViewConfig(roleId, items) {
  const res = await axios.put(
    `${constants.API_BASE_URL}/api/permissions/role/${roleId}/recordview-config`,
    { items },
    { headers: getCrmAuthHeaders() }
  );
  return res.data;
}

export async function fetchObjectMetaBatch(objectNames) {
  const headers = getCrmAuthHeaders();
  const results = await Promise.all(
    objectNames.map(async (on) => {
      try {
        const rvRes = await axios.get(
          `${constants.API_BASE_URL}/api/recordview/object/${encodeURIComponent(on)}`,
          { headers }
        );
        return {
          on,
          recordviews: rvRes.data?.success ? rvRes.data.data || [] : [],
          error: null,
        };
      } catch (e) {
        return {
          on,
          recordviews: [],
          error: e.response?.data?.message || e.message || 'Failed to load',
        };
      }
    })
  );
  const map = {};
  for (const r of results) {
    map[r.on] = { recordviews: r.recordviews, error: r.error };
  }
  return map;
}

export function configRowsToMapByObject(configRows) {
  const m = {};
  for (const row of configRows || []) {
    if (row?.object_name) m[row.object_name] = row;
  }
  return m;
}

export async function fetchLayoutConfigRows(roleId) {
  const res = await axios.get(
    `${constants.API_BASE_URL}/api/permissions/role/${roleId}/layout-config`,
    { headers: getCrmAuthHeaders() }
  );
  return res.data?.success ? res.data.data || [] : [];
}

/** PUT role layout-config; returns axios body `{ success, data?, message? }`. */
export async function saveLayoutConfig(roleId, items) {
  const res = await axios.put(
    `${constants.API_BASE_URL}/api/permissions/role/${roleId}/layout-config`,
    { items },
    { headers: getCrmAuthHeaders() }
  );
  return res.data;
}

/** object_name → { layouts, error } for layout pickers */
export async function fetchLayoutsForObjectNames(objectNames) {
  const headers = getCrmAuthHeaders();
  const names = [...new Set((objectNames || []).filter(Boolean))];
  const results = await Promise.all(
    names.map(async (on) => {
      try {
        const res = await axios.get(
          `${constants.API_BASE_URL}/api/objectconfig/layouts/${encodeURIComponent(on)}`,
          { headers }
        );
        return {
          on,
          layouts: res.data?.success ? res.data.data || [] : [],
          error: null,
        };
      } catch (e) {
        return {
          on,
          layouts: [],
          error: e.response?.data?.message || e.message || 'Failed to load',
        };
      }
    })
  );
   const map = {};
  for (const r of results) {
    // API returns layout_id (see layout.model fetchLayoutsByObject); normalize id for UI
    const layouts = filterLayoutsDetailOnly(
      (r.layouts || []).map((lo) => ({
        ...lo,
        id: lo.id ?? lo.layout_id,
      }))
    );
    map[r.on] = { layouts, error: r.error };
  }
  return map;
}

/** Build display strings for one object using saved config + metadata. */
export function formatRecordViewLayoutCells(cfg, meta) {
  const safeCfg = cfg || { recordview_ids: [] };
  const recordviews = meta?.recordviews || [];
  const rvLabels = (safeCfg.recordview_ids || [])
    .map((id) => {
      const r = recordviews.find((x) => String(x.id) === String(id));
      return r ? r.view_label || r.name || id : id;
    })
    .filter(Boolean);
  return {
    recordViews: rvLabels.length ? rvLabels.join(', ') : '—',
  };
}
